import { useState, useEffect } from "react"
import { useUpdateUserMutation, useDeleteUserMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faTrashCan, faUser, faUserGear, faEnvelope } from "@fortawesome/free-solid-svg-icons"
import { ROLES } from "../../config/roles"
import { toast } from 'react-toastify'


const USER_REGEX = /^[A-z]{3,20}$/
const PWD_REGEX = /^[A-z0-9!@#$%]{4,15}$/

const EditUserForm = ({ user, users }) => {

    // database updates
    const [updateUser, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useUpdateUserMutation()

    const [deleteUser, {
        isSuccess: isDelSuccess,
        isError: isDelError,
        error: delerror
    }] = useDeleteUserMutation()

    const navigate = useNavigate()

    // set state
    const [username, setUsername] = useState(user.username)
    const [firstname, setFirstname] = useState(user.firstname)
    const [lastname, setLastname] = useState(user.lastname)
    const [validUsername, setValidUsername] = useState(false)
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(false)
    const [roles, setRoles] = useState(user.roles)
    const [staff, setStaff] = useState(user.staff)
    const [active, setActive] = useState(user.active)
    const [clinic, setClinic] = useState(user.clinic)
    const [email, setEmail] = useState(user.email)
    const [phone, setPhone] = useState(user.phone)



    //check fields
    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(password))
    }, [password])


    useEffect(() => {
        if (isSuccess) {
            setUsername('')
            setFirstname('')
            setLastname('')
            setPassword('')
            setClinic('')
            setEmail('')
            setPhone('')
            setRoles([])
            toast.success("User was Updated");
            navigate('/dash/users')
        }

        if (isDelSuccess) {
            setUsername('')
            setFirstname('')
            setLastname('')
            setPassword('')
            setClinic('')
            setEmail('')
            setPhone('')
            setRoles([])
            toast.success("User was Deleted");
            navigate('/dash/users')
        }

        if (isError) {
            toast.error(error?.data?.message);
        }

        if (isDelError) {
            toast.error(delerror?.data?.message);
        }


    }, [isSuccess, isDelSuccess, navigate, isError, isDelError, error, delerror])


    // select options
    const optionRoles = Object.values(ROLES).map(role => {
        return (
            <option key={role} value={role}>
                {role}
            </option >
        )
    })

    // remove current user from staff list
    const staffNames = users.filter(item => item.id !== user.id)

    const optionStaff = staffNames.map(user => {
        return (
            <option key={user.id} value={user.id}>
                {user.lastname}, {user.firstname}
            </option >
        )
    })



    // changes and updates
    const onUsernameChanged = e => setUsername(e.target.value)
    const onFirstnameChanged = e => setFirstname(e.target.value)
    const onLastnameChanged = e => setLastname(e.target.value)
    const onPasswordChanged = e => setPassword(e.target.value)
    const onClinicChanged = e => setClinic(e.target.value)
    const onEmailChanged = e => setEmail(e.target.value)
    const onPhoneChanged = e => setPhone(e.target.value)

    const onRolesChanged = e => {
        const values = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        )
        setRoles(values)
    }

    const onStaffChanged = e => {
        const values = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        )
        setStaff(values)
    }

    const onActiveChanged = () => setActive(prev => !prev)



    // button clicks
    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            if (password) {
                await updateUser({ id: user.id, username, firstname, lastname, roles, staff, active, clinic, email, phone, password })
            } else {
                await updateUser({ id: user.id, username, firstname, lastname, roles, staff, active, clinic, email, phone })
            }
        } else {
            if (!validUsername) {
                toast.error("Username is not valid")
            } else if (!validPassword) {
                toast.error("Password is not valid")
            } else if (!roles.length) {
                toast.error("Select role(s)")
            } else {
                toast.error('Username, Firstname, and Lastname are required');
            }
        }
    }

    const onDeleteUserClicked = async () => {
        await deleteUser({ id: user.id })
    }

    const onUsersClicked = async () => {
        navigate('/dash/users')
    }

    const onEmailUserClicked = async () => {
        navigate(`/dash/emails/users/${user.id}`)
    }
    


    let canSave
    if (password) {
        canSave = [roles.length, validUsername, validPassword, firstname, lastname].every(Boolean) && !isLoading
    } else {
        canSave = [roles.length, validUsername, firstname, lastname].every(Boolean) && !isLoading
    }

    const content = (
        <section id="main_content">
            <header>
                <h1 className="text-steel mt-5"><FontAwesomeIcon icon={faUserGear} style={{fontSize:"30px", marginRight:"5px"}} />Edit User</h1>
            </header>
            <main>
                <form onSubmit={e => e.preventDefault()}>
                    <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="username">
                                Username: <span className="text-danger">*</span><br/>
                                <span>[3-20 letters]</span>
                            </label>
                            <input
                                className="form-control"
                                id="username"
                                name="username"
                                type="text"
                                autoComplete="off"
                                value={username}
                                onChange={onUsernameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="clinic">
                                Clinic<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="clinic"
                                name="clinic"
                                type="text"
                                autoComplete="off"
                                value={clinic}
                                onChange={onClinicChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-7">
                            <label className="form-label" htmlFor="password">
                                Password: <span className="text-danger">*</span><br/>
                                <span>[empty = no change]</span><span>[4-15 chars incl. !@#$%]</span>
                            </label>
                            <input
                                className="form-control"
                                id="password"
                                name="password"
                                type="password"
                                value={password}
                                onChange={onPasswordChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="email">
                                Email<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="off"
                                value={email}
                                onChange={onEmailChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="firstname">
                                Firstname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="firstname"
                                name="firstname"
                                type="text"
                                autoComplete="off"
                                value={firstname}
                                onChange={onFirstnameChanged}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label" htmlFor="lastname">
                                Lastname: <span className="text-danger">*</span><br/>
                                <span>[Required]</span>
                            </label>
                            <input
                                className="form-control"
                                id="lastname"
                                name="lastname"
                                type="text"
                                autoComplete="off"
                                value={lastname}
                                onChange={onLastnameChanged}
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label" htmlFor="phone">
                                Phone<br/><span style={{fontSize: "Small"}}>[Optional]</span>
                            </label>
                            <input
                                className="form-control"
                                id="phone"
                                name="phone"
                                type="text"
                                autoComplete="off"
                                value={phone}
                                onChange={onPhoneChanged}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-1">
                            <label className="form-check-label" htmlFor="user-active">
                                ACTIVE:
                            </label>
                        </div>
                        <div className="col-md-1">
                            <input
                                className="table-input"
                                id="user-active"
                                name="user-active"
                                type="checkbox"
                                checked={active}
                                onChange={onActiveChanged}
                            />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label text-end" htmlFor="roles">
                                Roles:
                            </label>
                        </div>
                        <div className="col-md-4">
                            <select
                                id="roles"
                                name="roles"
                                className="form-select"
                                multiple={true}
                                size="5"
                                value={roles}
                                onChange={onRolesChanged}
                            >
                                {optionRoles}
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label className="form-label text-end" htmlFor="roles">
                                Staff:
                            </label>
                        </div>
                        <div className="col-md-4">
                            <select
                                id="staff"
                                name="staff"
                                className="form-select"
                                multiple={true}
                                size="5"
                                value={staff}
                                onChange={onStaffChanged}
                            >
                                <option value="">N/A</option>
                                {optionStaff}
                            </select>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12 text-end">
                            <br/>
                            <span
                                role="button"
                                className="p-2 text-secondary icon"
                                title="Send Email"
                                onClick={onEmailUserClicked}
                            >
                                Email User
                                <FontAwesomeIcon icon={faEnvelope}  className="fa-icon"/>
                            </span>
                            <span
                                role="button"
                                className="p-2 text-success icon"
                                title="Save"
                                onClick={onSaveUserClicked}
                            >
                                Update User
                                <FontAwesomeIcon icon={faSave}  className="fa-icon"/>
                            </span>
                            <span
                                role="button"
                                className="p-2 text-danger icon"
                                title="Delete"
                                onClick={onDeleteUserClicked}
                            >
                                Delete
                                <FontAwesomeIcon icon={faTrashCan}  className="fa-icon"/>
                            </span>
                            <span
                                role="button"
                                className="p-2 text-info icon"
                                title="Back to Users"
                                onClick={onUsersClicked}
                            >
                                Back to Users
                                <FontAwesomeIcon icon={faUser}  className="fa-icon"/>
                            </span>
                        </div>
                    </div>
                </form>

            </main>
        </section>
    )

    return content
}
export default EditUserForm